import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import { PERMISSION } from 'src/features/auth';
import { useListPricingPlanTickRates } from 'src/features/pricingPlan/api/tickRate/useListPricingPlanTickRates';
import { useGetPricingPlan } from 'src/features/pricingPlan/api/useGetPricingPlan';
import CreateTickRateFormModal from 'src/features/pricingPlan/components/form/tickRate/CreateTickRateFormModal';
import CellTickRatesActions from 'src/features/pricingPlan/components/table/CellTickRatesActions';
import { DAY_OF_WEEK_TRANSLATIONS_MAP } from 'src/features/pricingPlan/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PRICING_PLAN_STATUS, PricingPlanTickRate } from 'src/features/pricingPlan/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSorting from 'src/registry/hooks/useSorting';

// The API sends the times with their seconds, while a band is read by the minute: "05:59:59" is shown as "05:59".
const formatHour = (time: string) => time.slice(0, 5);

const PricingPlanRatesTable = () => {
    const { formatMessage, formatNumber } = useIntl();
    const { planId = '' } = useParams();
    const { parkingId } = useParking();
    const settingsData = React.useContext(SettingsContext);

    const managePricingPlanTickRates = useHasPermissions([PERMISSION.MANAGE_PRICING_PLAN_TICK_RATES]);

    // Default (standard) plans are read-only — hide the "new entry" button.
    const { data: pricingPlan } = useGetPricingPlan({
        planId,
        parkingId,
        config: { enabled: !!planId && !!parkingId },
    });
    const isSystem = !!pricingPlan?.is_system;

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'start_day', desc: false }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const {
        data: pricingPlanTickRatesData,
        isLoading,
        isError,
    } = useListPricingPlanTickRates({
        planId,
        parkingId,
        ...queryFiltersOptions,
        ...querySortingOptions,
        ...queryPaginationOptions,
        config: {
            enabled: !!planId && !!parkingId,
        },
    });

    const columnHelper = createColumnHelper<PricingPlanTickRate>();

    const columns = [
        columnHelper.accessor('start_day', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            cell: ({ row }) =>
                `${formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[row.original.start_day])} ${formatHour(row.original.from_time)}`,
            meta: {
                title: formatMessage(pricingPlanMessages.fromHour),
                inputType: INPUT_TYPE.MULTI_SELECT,
                options: Object.entries(DAY_OF_WEEK_TRANSLATIONS_MAP).map(([key, value]) => ({
                    label: formatMessage(value),
                    value: key,
                })),
            },
        }),
        columnHelper.accessor('end_day', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            cell: ({ row }) =>
                `${formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[row.original.end_day])} ${formatHour(row.original.to_time)}`,
            meta: {
                title: formatMessage(pricingPlanMessages.toHour),
                inputType: INPUT_TYPE.MULTI_SELECT,
                options: Object.entries(DAY_OF_WEEK_TRANSLATIONS_MAP).map(([key, value]) => ({
                    label: formatMessage(value),
                    value: key,
                })),
            },
        }),
        columnHelper.accessor('rate_with_tax', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: ({ row }) => {
                const value = row.original.rate_with_tax;
                return formatNumber(value / 100, {
                    style: 'currency',
                    currency: row.original.currency ?? settingsData.default_currency,
                });
            },
            meta: {
                title: formatMessage(pricingPlanMessages.rate),
            },
        }),
        columnHelper.accessor('max_amount', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: ({ row }) => {
                const value = row.original.max_amount;
                if (value == null) return '-';

                return formatNumber(value / 100, {
                    style: 'currency',
                    currency: row.original.currency ?? settingsData.default_currency,
                });
            },
            meta: {
                title: formatMessage(pricingPlanMessages.maxAmount),
            },
        }),
        ...(managePricingPlanTickRates && !isSystem
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: (props) => (
                          <CellTickRatesActions
                              {...props}
                              isPricingPlanPublished={pricingPlan?.status === PRICING_PLAN_STATUS.PUBLISHED}
                          />
                      ),
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<PricingPlanTickRate, any>
            isLoading={isLoading}
            isError={isError}
            columns={columns}
            data={pricingPlanTickRatesData}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            enableSearchParams={false}
            semantics={{
                title: formatMessage(pricingPlanMessages.rates),
                entryName: formatMessage(pricingPlanMessages.rate).toLowerCase(),
                entryNames: formatMessage(pricingPlanMessages.rates).toLowerCase(),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(managePricingPlanTickRates && !isSystem && { newEntry: CreateTickRateFormModal }),
            }}
        />
    );
};

export default PricingPlanRatesTable;
